
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { format } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, Clock, FileText, Image, User } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { StatusUpdateDialog } from "./StatusUpdateDialog";
import { useState } from "react";
import { FileUploadSection } from "./FileUploadSection";
import { getPreviewUrl } from "@/utils/imageUtils";

type DigitizationStatus = Database["public"]["Enums"]["digitization_status"];

interface RequestDetailsSheetProps {
  request: {
    request_id: number;
    title: string;
    description: string | null;
    art_technique: string | null;
    creation_date: string | null;
    location: string | null;
    preview: string;
    status: DigitizationStatus;
    notes: string | null;
    created_at: string | null;
    updated_at: string | null;
    raw_file_id: number | null;
    processed_file_id: number | null;
    creators: {
      name: string;
      surname: string;
    } | null;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusUpdate: () => void;
}

export function RequestDetailsSheet({ 
  request, 
  open, 
  onOpenChange,
  onStatusUpdate 
}: RequestDetailsSheetProps) {
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  if (!request) return null;

  const getStatusBadgeColor = (status: DigitizationStatus) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500";
      case "in_progress":
        return "bg-blue-500";
      case "completed":
        return "bg-green-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[400px] sm:w-[540px] sm:max-w-[90vw]">
          <SheetHeader>
            <SheetTitle>Digitization Request Details</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-80px)] pr-4">
            <div className="space-y-6 py-6">
              {/* Status Section */}
              <div className="flex items-center justify-between">
                <Badge className={getStatusBadgeColor(request.status)}>
                  {request.status}
                </Badge>
                <Button 
                  variant="outline" 
                  onClick={() => setStatusDialogOpen(true)}
                >
                  Update Status
                </Button>
              </div>

              {/* File Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    File Upload
                  </CardTitle>
                  <CardDescription>
                    Upload the raw and processed files for digitization
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Raw File</h4>
                    <FileUploadSection 
                      requestId={request.request_id} 
                      onUploadComplete={onStatusUpdate}
                    />
                    {request.raw_file_id && (
                      <p className="text-sm text-muted-foreground">
                        Raw file uploaded successfully
                      </p>
                    )}
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Processed File</h4>
                    <FileUploadSection 
                      requestId={request.request_id} 
                      onUploadComplete={onStatusUpdate}
                      isProcessed
                    />
                    {request.processed_file_id && (
                      <p className="text-sm text-muted-foreground">
                        Processed file uploaded successfully
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Preview Image */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <img
                    src={getPreviewUrl(request.preview)}
                    alt={request.title}
                    className="w-full rounded-lg object-cover"
                  />
                </CardContent>
              </Card>

              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Request Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium">Title</h4>
                    <p className="text-sm text-muted-foreground">{request.title}</p>
                  </div>
                  {request.description && (
                    <div>
                      <h4 className="text-sm font-medium">Description</h4>
                      <p className="text-sm text-muted-foreground">{request.description}</p>
                    </div>
                  )}
                  {request.art_technique && (
                    <div>
                      <h4 className="text-sm font-medium">Art Technique</h4>
                      <p className="text-sm text-muted-foreground">{request.art_technique}</p>
                    </div>
                  )}
                  {request.creation_date && (
                    <div>
                      <h4 className="text-sm font-medium">Creation Date</h4>
                      <p className="text-sm text-muted-foreground">{request.creation_date}</p>
                    </div>
                  )}
                  {request.location && (
                    <div>
                      <h4 className="text-sm font-medium">Location</h4>
                      <p className="text-sm text-muted-foreground">{request.location}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Creator Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Creator Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {request.creators?.name} {request.creators?.surname}
                  </p>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Timestamps
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Created</p>
                      <p className="text-sm text-muted-foreground">
                        {request.created_at
                          ? format(new Date(request.created_at), "PPp")
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Last Updated</p>
                      <p className="text-sm text-muted-foreground">
                        {request.updated_at
                          ? format(new Date(request.updated_at), "PPp")
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              {request.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{request.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <StatusUpdateDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        currentStatus={request.status}
        requestId={request.request_id}
        onStatusUpdated={() => {
          setStatusDialogOpen(false);
          onStatusUpdate();
        }}
      />
    </>
  );
}
